import React, { useState, useEffect } from 'react';
import { financialApi, projectApi } from '../services/api';
import { Card, Select, Spinner, Badge } from '../components/UI';
import { ArrowUp, ArrowDown, Equals, ChartBar } from '@phosphor-icons/react';

import { fmtCurrencyShort } from '../utils/currencyFormat';

export default function BudgetVsActualPage() {
  const [data, setData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { projectApi.list().then(r => setProjects(r.data)).finally(() => setLoading(false)); }, []);

  useEffect(() => {
    financialApi.budgetVsActual(selectedProject || undefined).then(r => setData(r.data));
  }, [selectedProject]);

  const fmt = (n) => fmtCurrencyShort(n);

  const totals = data.reduce((acc, d) => ({
    budget: acc.budget + d.total_budget,
    actual: acc.actual + d.total_actual,
    variance: acc.variance + d.variance
  }), { budget: 0, actual: 0, variance: 0 });

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="budget-vs-actual-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Budget vs Actual</h1>
          <p className="text-sm text-[#666]">Compare planned budgets against actual costs</p>
        </div>
        <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="w-64" data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      {/* Summary */}
      {data.length > 0 && (
        <div className="grid grid-cols-3 gap-3" data-testid="bva-summary">
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-center">
            <p className="text-[10px] text-[#999] uppercase">Total Budget</p>
            <p className="text-lg font-bold text-[#002FA7]">{fmt(totals.budget)}</p>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-center">
            <p className="text-[10px] text-[#999] uppercase">Total Actual</p>
            <p className="text-lg font-bold text-[#0A0A0A]">{fmt(totals.actual)}</p>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-center">
            <p className="text-[10px] text-[#999] uppercase">Variance</p>
            <p className={`text-lg font-bold ${totals.variance >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`}>
              {totals.variance >= 0 ? '+' : ''}{fmt(totals.variance)}
            </p>
          </div>
        </div>
      )}

      {data.length === 0 ? (
        <Card className="p-8 text-center text-[#666]">
          <ChartBar className="w-10 h-10 mx-auto mb-3 text-[#E0E0E0]" />
          <p>No budget data. Set budgets first, then derive costs from execution.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="bva-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] text-[#999] uppercase tracking-wider bg-[#F9F9F9] border-b border-[#E0E0E0]">
                  <th className="px-4 py-2.5">Activity</th>
                  <th className="px-3 py-2.5 text-right">Material B</th>
                  <th className="px-3 py-2.5 text-right">Material A</th>
                  <th className="px-3 py-2.5 text-right">Labor B</th>
                  <th className="px-3 py-2.5 text-right">Labor A</th>
                  <th className="px-3 py-2.5 text-right">Other B</th>
                  <th className="px-3 py-2.5 text-right">Other A</th>
                  <th className="px-3 py-2.5 text-right">Total Budget</th>
                  <th className="px-3 py-2.5 text-right">Total Actual</th>
                  <th className="px-4 py-2.5 text-right">Variance</th>
                </tr>
              </thead>
              <tbody>
                {data.map(d => (
                  <tr key={d.budget_id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`bva-row-${d.budget_id}`}>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-[#0A0A0A]">{d.activity_name}</p>
                      <p className="text-[10px] text-[#999]">{d.project_name}</p>
                    </td>
                    <td className="px-3 py-2.5 text-right text-[#666]">{fmt(d.material_budget)}</td>
                    <td className="px-3 py-2.5 text-right">{fmt(d.material_actual)}</td>
                    <td className="px-3 py-2.5 text-right text-[#666]">{fmt(d.labor_budget)}</td>
                    <td className="px-3 py-2.5 text-right">{fmt(d.labor_actual)}</td>
                    <td className="px-3 py-2.5 text-right text-[#666]">{fmt(d.other_budget)}</td>
                    <td className="px-3 py-2.5 text-right">{fmt(d.other_actual)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#002FA7]">{fmt(d.total_budget)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold">{fmt(d.total_actual)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {d.variance > 0 ? <ArrowUp className="w-3 h-3 text-[#00C853]" /> :
                         d.variance < 0 ? <ArrowDown className="w-3 h-3 text-[#FF3333]" /> :
                         <Equals className="w-3 h-3 text-[#999]" />}
                        <span className={`font-bold ${d.variance >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`}>
                          {fmt(d.variance)}
                        </span>
                        <span className="text-[10px] text-[#999] ml-1">({d.variance_pct}%)</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
